import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Buildings, CaretDown, ArrowsClockwise, PencilSimple, Trash } from "@phosphor-icons/react";
import { useRef } from "react";

function SignatureCard() {
  const [sig, setSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me/signature");
        setSig(data.signature || "");
      } catch (e) { /* ignore — signature is optional */ }
    })();
  }, []);

  const persist = async (dataUrl) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/signature", { signature: dataUrl });
      setSig(data.signature);
      toast.success("Signature image saved — will appear beside your digital sign-off stamp");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) return toast.error("Signature must be an image");
    if (f.size > 400 * 1024) return toast.error("Signature must be under 400 KB");
    const reader = new FileReader();
    reader.onload = () => persist(String(reader.result));
    reader.readAsDataURL(f);
  };

  const clear = async () => {
    if (!window.confirm("Remove your saved signature image? Your digital sign-off stamp is unaffected.")) return;
    try {
      await api.delete("/users/me/signature");
      setSig("");
      toast.success("Image cleared — digital sign-off stamp remains active");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="card-flat p-6 max-w-xl" data-testid="signature-card">
      <div className="label-caps mb-1 flex items-center gap-1">
        <PencilSimple size={12} /> Digital sign-off
      </div>
      <div className="text-lg font-display uppercase text-navy">O365 Signature Stamp</div>
      <div className="mt-3 border-l-4 border-emerald-500 bg-emerald-50 p-3 text-xs text-emerald-900">
        <b>Your signature is your Microsoft O365 identity.</b> Every time you approve a document, a
        cryptographic stamp is added carrying <span className="font-mono">your name · email · O365-verified · timestamp · version</span>.
        No drawing or upload required — the auth-backed stamp <i>is</i> the signature.
      </div>
      <p className="text-xs text-slate-500 mt-4 mb-2">
        Optional: upload a scanned wet-ink signature (PNG/JPG, ≤ 400 KB). It will appear <b>next to</b>
        your digital stamp as visual flair — the O365 stamp remains the source of truth.
      </p>
      {sig ? (
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 flex items-center gap-3">
          <img src={sig} alt="my signature" data-testid="signature-preview"
               className="max-h-16 max-w-[220px] bg-white p-1 rounded-sm border border-slate-200" />
          <div className="text-[11px] text-slate-500 font-mono">image saved</div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => fileRef.current?.click()} data-testid="signature-replace"
              className="text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-white">Replace</button>
            <button onClick={clear} data-testid="signature-clear"
              className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded-sm hover:bg-red-50 inline-flex items-center gap-1">
              <Trash size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => fileRef.current?.click()} data-testid="signature-upload"
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold text-navy border border-navy rounded-sm hover:bg-navy hover:text-white disabled:opacity-40">
          <PencilSimple size={14} /> {saving ? "Saving…" : "Upload wet-ink image (optional)"}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="signature-file-input" />
    </div>
  );
}

function DepartmentPicker({ userId, current, deps, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(current || []);

  useEffect(() => { setSelected(current || []); }, [current]);

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    try {
      await api.patch(`/users/${userId}/departments`, { department_ids: selected });
      toast.success("Departments updated");
      onChange();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const label = !current?.length
    ? "None assigned"
    : current.length === 1
      ? deps.find((d) => d.id === current[0])?.name || "1 dept"
      : `${current.length} depts`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          data-testid={`dept-picker-${userId}`}
          className="inline-flex items-center gap-2 h-8 px-3 text-xs border border-slate-300 rounded-sm hover:border-cyan bg-white disabled:opacity-50"
        >
          <Buildings size={14} className="text-cyan" />
          <span className="truncate max-w-[180px]">{label}</span>
          <CaretDown size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="px-3 py-2 border-b bg-slate-50">
          <div className="label-caps">Assign departments</div>
          <p className="text-[11px] text-slate-500 mt-0.5">Empty = no access. Admins/managers ignore this list.</p>
        </div>
        <div className="max-h-64 overflow-y-auto p-2">
          {deps.map((d) => (
            <label
              key={d.id}
              data-testid={`dept-option-${userId}-${d.id}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(d.id)}
                onCheckedChange={() => toggle(d.id)}
              />
              <span className="text-sm text-zinc-800">{d.name}</span>
            </label>
          ))}
        </div>
        <div className="p-2 border-t flex items-center justify-end gap-2">
          <button onClick={() => { setSelected(current || []); setOpen(false); }} className="text-xs px-3 py-1.5 text-slate-500 hover:text-navy">Cancel</button>
          <button
            onClick={save}
            data-testid={`dept-save-${userId}`}
            className="text-xs px-3 py-1.5 bg-navy text-white rounded-sm hover:bg-navy/90"
          >
            Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [deps, setDeps] = useState([]);
  const [selfCard, setSelfCard] = useState(null);
  const isPrivileged = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    // Operators (and anyone without admin/manager) are not allowed to list
    // other users. Show them a self-service card instead of firing the
    // admin-only /users endpoint (which returns 403).
    if (!isPrivileged) {
      try {
        const me = await api.get("/auth/me");
        setSelfCard(me.data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
      return;
    }
    try {
      const [u, d] = await Promise.all([api.get("/users"), api.get("/departments")]);
      setUsers(u.data);
      setDeps(d.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const syncMe = async () => {
    try {
      const { data } = await api.post(`/users/me/sync-directory`);
      if (data.matched) {
        toast.success(`Synced: ${data.user.designation || "—"} · ${data.user.department_name || "—"} — reload to refresh header`);
        setSelfCard(data.user);
      } else {
        toast.warning(data.detail || "No corporate directory match — your email may not be in HR's system");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const syncDirectory = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/sync-directory`);
      if (data.matched) {
        toast.success(`Synced: ${data.user.designation || "—"} · ${data.user.department_name || "—"}`);
        load();
      } else {
        toast.warning(data.detail || "No corporate directory match");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const [syncingAll, setSyncingAll] = useState(false);
  const syncAllDirectory = async () => {
    if (syncingAll) return;
    if (!window.confirm("Sync ALL users' designation & department from the corporate directory now?")) return;
    setSyncingAll(true);
    try {
      const { data } = await api.post(`/users/sync-all-directory`);
      const errs = (data.errors || []).length;
      const msg = `Synced ${data.matched}/${data.total} — ${data.unmatched} not in directory${errs ? `, ${errs} error(s)` : ""}`;
      if (data.matched > 0) toast.success(msg);
      else toast.warning(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {!isPrivileged ? (
        <>
          <div>
            <div className="label-caps">Your profile</div>
            <h2 className="font-display text-3xl text-navy uppercase mt-1">My HR record</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Only admins and managers can see the full user roster. Use the button below to refresh
              your own designation &amp; department from the corporate directory.
            </p>
          </div>
          <div className="card-flat p-6 max-w-xl" data-testid="self-hr-card">
            <div className="text-lg font-display uppercase text-navy" data-testid="self-name">
              {selfCard?.name || user?.name || "—"}
            </div>
            <div className="text-xs font-mono text-slate-500 mb-4" data-testid="self-email">
              {selfCard?.email || user?.email || ""}
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-slate-500">Designation</dt>
              <dd className="text-navy font-medium" data-testid="self-designation">
                {selfCard?.designation || <span className="italic text-slate-400">Not set</span>}
              </dd>
              <dt className="text-slate-500">Department</dt>
              <dd className="text-navy font-medium" data-testid="self-department">
                {selfCard?.department_name || <span className="italic text-slate-400">Not set</span>}
              </dd>
              {selfCard?.employee_no && (
                <>
                  <dt className="text-slate-500">Employee #</dt>
                  <dd className="font-mono text-slate-800" data-testid="self-empno">{selfCard.employee_no}</dd>
                </>
              )}
              {selfCard?.reporting_manager && (
                <>
                  <dt className="text-slate-500">Reports to</dt>
                  <dd className="text-slate-800">{selfCard.reporting_manager}</dd>
                </>
              )}
              {selfCard?.synced_from_emp_at && (
                <>
                  <dt className="text-slate-500">Last synced</dt>
                  <dd className="font-mono text-[11px] text-slate-500">
                    {new Date(selfCard.synced_from_emp_at).toLocaleString()}
                  </dd>
                </>
              )}
            </dl>
            <button
              onClick={syncMe}
              data-testid="self-sync-btn"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold text-white bg-navy rounded-sm hover:bg-navy-dark"
            >
              <ArrowsClockwise size={14} weight="bold" /> Sync my HR record
            </button>
          </div>
          <SignatureCard />
        </>
      ) : (
      <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps">Access control</div>
          <h2 className="font-display text-3xl text-navy uppercase mt-1">Users & roles</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Admins and managers see every document. <span className="font-semibold text-navy">Operators are scoped to only the departments listed on their row</span> — assign them to the shop floors they own.
          </p>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={syncAllDirectory}
            disabled={syncingAll}
            data-testid="sync-all-directory"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold text-white bg-navy rounded-sm hover:bg-navy-dark disabled:opacity-40"
          >
            <ArrowsClockwise size={14} weight="bold" className={syncingAll ? "animate-spin" : ""} />
            {syncingAll ? "Syncing…" : "Sync all users"}
          </button>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-zinc-200 bg-zinc-50">
              <th className="px-4 py-3 label-caps">Name</th>
              <th className="px-4 py-3 label-caps">Email</th>
              <th className="px-4 py-3 label-caps">Designation · Dept</th>
              <th className="px-4 py-3 label-caps">Role</th>
              <th className="px-4 py-3 label-caps">Department access</th>
              <th className="px-4 py-3 label-caps text-center">HR sync</th>
              <th className="px-4 py-3 label-caps">Joined</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="dms-row border-b border-zinc-100" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">
                  {u.name}
                  {u.employee_no && (
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                      #{u.employee_no}{u.employee_company ? ` · ${u.employee_company}` : ""}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-zinc-700 text-xs">{u.email}</td>
                <td className="px-4 py-3 text-xs" data-testid={`user-hr-${u.id}`}>
                  {u.designation || u.department_name ? (
                    <>
                      <div className="font-semibold text-navy">{u.designation || "—"}</div>
                      <div className="text-zinc-600 mt-0.5">
                        {u.department_name || "—"}
                        {u.sub_department ? ` · ${u.sub_department}` : ""}
                      </div>
                      {u.reporting_manager && (
                        <div className="text-[10px] font-mono text-zinc-400 mt-0.5">→ {u.reporting_manager}</div>
                      )}
                    </>
                  ) : (
                    <span className="italic text-zinc-400">Not synced</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {user?.role === "admin" && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                      <SelectTrigger className="w-36 h-8" data-testid={`role-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="operator">Operator</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm text-blue-700 bg-blue-50 border-blue-200">
                      {u.role}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role === "operator" ? (
                    <DepartmentPicker
                      userId={u.id}
                      current={u.department_ids || []}
                      deps={deps}
                      onChange={load}
                      disabled={user?.role !== "admin"}
                    />
                  ) : (
                    <span className="text-xs text-slate-500 italic">All departments</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => syncDirectory(u.id)}
                    data-testid={`sync-user-${u.id}`}
                    title="Refresh designation + department from corporate directory"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-slate-300 text-navy hover:bg-slate-50"
                  >
                    <ArrowsClockwise size={14} />
                  </button>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-500 text-xs">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}
